import type { GetDdqStructureQuery, QuestionType } from "~/graphql/supergraphTypes";

export type FlatSection = GetDdqStructureQuery["ddqSections"][number];
export type FlatQuestion = GetDdqStructureQuery["ddqQuestions"][number];

export type TreeQuestion = {
  id: string;
  number: string;
  questionText: string;
  questionType: QuestionType;
  questionNotes: string | null;
  children: TreeQuestion[];
};

export type TreeSection = {
  id: string;
  number: string;
  title: string;
  questions: TreeQuestion[];
  subsections: TreeSection[];
};

const groupBy = <T, K>(items: T[], key: (item: T) => K): Map<K, T[]> => {
  const groups = new Map<K, T[]>();
  for (const item of items) {
    const k = key(item);
    groups.get(k)?.push(item) ?? groups.set(k, [item]);
  }
  return groups;
};

const byPosition = <T extends { position: number }>(items: T[]): T[] =>
  items.slice().sort((a, b) => a.position - b.position);

// Both lists arrive flat; sections nest via parentSectionId (one level, FR-056) and questions
// via sectionId plus parentQuestionId (conditional nesting, unlimited depth, FR-082). Numbering
// is display-only (FR-072/D-09) — ids, never numbers, are what the rest of the app should key on.
export const buildTree = (
  sections: FlatSection[],
  questions: FlatQuestion[],
): TreeSection[] => {
  const sectionsByParent = groupBy(sections, (section) => section.parentSectionId);
  const questionsByParent = groupBy(questions, (question) => question.parentQuestionId);

  const buildQuestions = (
    sectionId: string,
    parentQuestionId: string | null,
    numberPrefix: string,
  ): TreeQuestion[] => {
    const candidates = questionsByParent.get(parentQuestionId) ?? [];
    const siblings = byPosition(
      parentQuestionId === null
        ? candidates.filter((question) => question.sectionId === sectionId)
        : candidates,
    );

    return siblings.map((question, index) => {
      const number = `${numberPrefix}${index + 1}`;
      return {
        id: question.id,
        number,
        questionText: question.questionText,
        questionType: question.questionType,
        questionNotes: question.questionNotes ?? null,
        children: buildQuestions(sectionId, question.id, `${number}.`),
      };
    });
  };

  const buildSections = (parentSectionId: string | null, numberPrefix: string): TreeSection[] =>
    byPosition(sectionsByParent.get(parentSectionId) ?? []).map((section, index) => {
      const number = `${numberPrefix}${index + 1}`;
      return {
        id: section.id,
        number,
        title: section.title,
        questions: buildQuestions(section.id, null, ""),
        subsections: buildSections(section.id, `${number}.`),
      };
    });

  return buildSections(null, "");
};
